import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FiImage, FiLifeBuoy, FiSend, FiUploadCloud, FiX } from "react-icons/fi";
import { useAltcha, Honeypot } from "components/core/Altcha/AltchaWidget";
import { RaiseQuery } from "./RaiseQueryForm.api";
import { toastrError, toastrSuccess } from "../../lib/toastr";
import { useAuth } from "context/auth-context";
import { PageHeader } from "components/ui";
import { getPendingQuery } from "pages/UserQueries/MyQueries.api";
import { PendingQueryNotice } from "./PendingQueryNotice";

export function RaiseQueryForm() {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const initialState = {
    name: auth.name,
    email: auth.email,
    query: "",
    errors: {
      name: "",
      email: "",
      query: "",
      image: "",
    },
    image_upload: [],
    // Captcha payload and honeypot travel with the request.
    altcha: "",
    contactReason: "",
  };
  type FormState = typeof initialState;
  type RaiseQueryPayload = Omit<FormState, "errors">;

  const [formState, setFormState] = useState<FormState>(initialState);
  // A member may have one query waiting for support at a time. While it is Open the form is
  // replaced by a "please wait" notice; the backend enforces the same rule on submit.
  const pendingQuery = useQuery(["pendingQuery", auth?.id], getPendingQuery, { enabled: !!auth?.id });
  const pending = pendingQuery.data;
  const { widget: altchaWidget, solve: solveAltcha } = useAltcha();
  const postQuery = useMutation(
    (payload: RaiseQueryPayload) => {
      return RaiseQuery({
        userId: auth.id,
        name: payload.name,
        email: payload.email,
        query: payload.query,
        image_upload: payload.image_upload,
        altcha: payload.altcha,
        contactReason: payload.contactReason,
      });
    },
    {
      onSuccess: (response: any) => {
        if (response.success) {
          toastrSuccess(t("help.successmsg"));
          setFormState(initialState);
          void pendingQuery.refetch();
        } else {
          toastrError(response.message);
        }
      },
      onError: (err: any) => {
        const response: any = err.response;
        toastrError(response?.data.message);
        // Refused because a query is already waiting: show the notice instead of the form.
        if (response?.status === 409) void pendingQuery.refetch();
      },
    }
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagesUploaded, setImagesUploaded] = useState<string[]>([]);
  const [imageError, setImageError] = useState("");
  const [imageLimitError, setImageLimitError] = useState("");
  const [imageDuplicate, setImageDuplicate] = useState("");
  const [issubmit, setissubmit] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = 4 - imagesUploaded.length;
    const filesToUpload = files.slice(0, remainingSlots);
    const totalImages = imagesUploaded.length + files.length;
    const uploadedImageNames = filesToUpload.map((file) => file.name);
    const duplicates = uploadedImageNames.filter((name) => imagesUploaded.includes(name));
    if (duplicates.length > 0) {
      return setImageDuplicate(t("help.DuplicateImage") + `${duplicates.join(", ")}`);
    } else {
      setImageDuplicate("");
    }
    if (totalImages > 4) {
      return setImageLimitError(t("help.ImageLimitError"));
    } else {
      setImageLimitError("");

      const imageFiles = filesToUpload.filter((file) => file.type.startsWith("image/"));
      if (imageFiles.length !== filesToUpload.length) {
        setImageError(t("help.ImageError"));
        return;
      }
      setImageError("");
      const uploadedImages = filesToUpload.map((file) => (file as File).name);
      setImagesUploaded([...imagesUploaded, ...uploadedImages]);
      setFormState((prevState) => ({
        ...prevState,
        image_upload: [...prevState.image_upload, ...filesToUpload],
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...imagesUploaded];
    newImages.splice(index, 1);
    setImagesUploaded(newImages);

    setFormState((prevState) => ({
      ...prevState,
      image_upload: prevState.image_upload.filter((_, i) => i !== index),
    }));
    if (imageDuplicate) {
      setImageDuplicate("");
    } else if (imageLimitError) {
      setImageLimitError("");
    }
  };

  const validateForm = (formValues: FormState) => {
    const errors = { name: "", email: "", query: "", image: "" };

    if (!formValues.query.length) {
      errors.query = t("home.raiseQuery.validQuery");
    } else if (!formValues.query.trim()) {
      errors.query = t("help.raise.blankSpacesNotAllowed");
    }

    setFormState((oldState) => ({
      ...oldState,
      errors,
    }));

    return !errors.query;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setissubmit(true);
    const isValid = validateForm(formState);
    if (isValid) {
      const altcha = await solveAltcha();
      if (!altcha) {
        toastrError("Captcha verification failed. Please try again.");
        return;
      }
      postQuery.mutate({ ...formState, altcha });
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormState({
      ...formState,
      [name]: value,
    });

    if (name === "query") {
      validateForm({ ...formState, [name]: value });
    }
  };

  useEffect(() => {
    if (issubmit) {
      validateForm(formState);
    }
  }, [t]);

  useEffect(() => {
    if (imageError) {
      setImageError(t("help.ImageError"));
    } else if (imageLimitError) {
      setImageLimitError(t("help.ImageLimitError"));
    } else if (imageDuplicate) {
      setImageDuplicate(t("help.DuplicateImage"));
    }
  }, [t]);

  useEffect(() => {
    if (postQuery.isSuccess) {
      setImagesUploaded([]);
    }
  }, [postQuery.isSuccess]);

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <FiLifeBuoy aria-hidden="true" />
            Help center
          </>
        }
        title={t("help.Support")}
        subtitle={t("help.query")}
      />
      {pending ? <PendingQueryNotice pending={pending} /> : <section className="zs-card">
        <form className="flex flex-col gap-5 p-5 sm:p-7" onSubmit={handleSubmit}>
          <div className="flex w-full">{altchaWidget}</div>
          <Honeypot
            value={formState.contactReason}
            onChange={(v) => setFormState((old) => ({ ...old, contactReason: v }))}
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="Name" aria-label="Name" className="zs-label">
                {t("help.Name")}
              </label>
              <input
                type="text"
                name="name"
                pattern="[a-zA-Z\s]{1,50}"
                id="name"
                value={formState.name}
                disabled={true}
                className="zs-input"
              />
              {formState.errors.name ? (
                <p className="zs-error" id="emai_err_msg">
                  {formState.errors.name}
                </p>
              ) : null}
            </div>
            <div>
              <label htmlFor="Email" aria-label="Email" className="zs-label">
                {t("page.help.Email")}
              </label>
              <input
                type="text"
                name="email"
                id="email"
                value={formState.email}
                disabled={true}
                className="zs-input"
              />
              {formState.errors.email ? (
                <p className="zs-error" id="emai_err_msg">
                  {formState.errors.email}
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <label htmlFor="query" className="zs-label zs-label-required">
              {t("help.WriteQuery")}
            </label>
            <textarea
              name="query"
              id="query"
              value={formState.query}
              onChange={handleChange}
              aria-invalid={formState.errors.query ? true : undefined}
              className={`zs-textarea min-h-[160px] leading-relaxed ${formState.errors.query ? "border-danger" : ""}`}
              placeholder={t("help.placeholder.query")}
            />
            {formState.errors.query ? (
              <p className="zs-error" id="emai_err_msg">
                {formState.errors.query}
              </p>
            ) : null}
          </div>

          <div>
            <label className="zs-label">{t("help.UploadScreenshot")}</label>
            <div className="flex flex-col gap-3">
              <label
                htmlFor="fileInput"
                className="flex flex-col items-center justify-center gap-2 px-4 py-8 text-center transition-colors border-2 border-dashed rounded-lg cursor-pointer border-line-strong bg-surface-2 hover:border-primary hover:bg-primary-soft"
              >
                <span className="zs-stat-icon w-11 h-11 text-xl">
                  <FiUploadCloud aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold text-primary-text">{t("help.uploadhere")}</span>
                <span className="text-xs text-ink-3">Images only · up to 4 files</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {imageError && <p className="w-full mt-0 zs-error">{imageError}</p>}
                {imagesUploaded.map((image, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center h-8 max-w-full gap-2 pl-3 pr-1 text-xs border rounded-full border-line bg-surface text-ink-2"
                  >
                    <FiImage aria-hidden="true" className="shrink-0 text-ink-3" />
                    <span className="truncate max-w-[200px]">{image}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${image}`}
                      className="inline-flex items-center justify-center w-6 h-6 transition-colors rounded-full shrink-0 text-ink-3 hover:bg-danger-soft hover:text-danger"
                      onClick={() => handleRemoveImage(index)}
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
              <input
                type="file"
                id="fileInput"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
                multiple
                accept="image/*"
              />
            </div>
            {imageLimitError && <p className="zs-error">{imageLimitError}</p>}
            {imageDuplicate && <p className="zs-error">{imageDuplicate}</p>}
          </div>

          <div className="flex justify-end pt-5 border-t border-line" data-testid="button">
            <button
              type="submit"
              className="w-full zs-btn zs-btn-primary zs-btn-lg sm:w-auto sm:min-w-[180px]"
              disabled={postQuery.isLoading}
            >
              {postQuery.isLoading ? <span className="zs-spinner border-ink-inverse/30 border-t-ink-inverse" aria-hidden="true" /> : <FiSend aria-hidden="true" />}
              {postQuery.isLoading ? t("page.Loading") : t("help.Submit")}
            </button>
          </div>
        </form>
      </section>}
    </>
  );
}
